import { Fragment, type ReactNode } from "react";

/**
 * Мінімальний БЕЗПЕЧНИЙ markdown→JSX рендер для відповідей помічника
 * (0 dangerouslySetInnerHTML). Підтримує: ## / ### заголовки, **bold**,
 * `code`, нумеровані (1.) та марковані (-, *) списки, абзаци.
 *
 * Свідомо НЕ підтримує HTML/посилання-ін'єкції — KLLM-вивід рендериться як
 * структурований текст, не як розмітка довіреного джерела.
 */

function renderInline(text: string, keyPrefix: string): ReactNode[] {
  // Розбиваємо на **bold** та `code`, решта — звичайний текст.
  const nodes: ReactNode[] = [];
  const re = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith("**")) {
      nodes.push(<strong key={`${keyPrefix}-b-${i}`}>{tok.slice(2, -2)}</strong>);
    } else {
      nodes.push(
        <code
          key={`${keyPrefix}-c-${i}`}
          className="rounded border bg-muted px-1 py-0.5 font-mono text-[11px] text-primary-dark"
        >
          {tok.slice(1, -1)}
        </code>,
      );
    }
    last = m.index + tok.length;
    i += 1;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

type Block =
  | { kind: "h"; level: 2 | 3; text: string }
  | { kind: "p"; text: string }
  | { kind: "ol"; items: string[] }
  | { kind: "ul"; items: string[] };

function parseBlocks(md: string): Block[] {
  const lines = md.replace(/\r\n/g, "\n").split("\n");
  const blocks: Block[] = [];
  let para: string[] = [];

  const flushPara = () => {
    if (para.length) {
      blocks.push({ kind: "p", text: para.join(" ") });
      para = [];
    }
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (line === "") {
      flushPara();
      continue;
    }
    const h = /^(#{2,3})\s+(.*)$/.exec(line);
    if (h) {
      flushPara();
      blocks.push({ kind: "h", level: h[1].length as 2 | 3, text: h[2] });
      continue;
    }
    const ol = /^\d+[.)]\s+(.*)$/.exec(line);
    if (ol) {
      flushPara();
      const prev = blocks[blocks.length - 1];
      if (prev && prev.kind === "ol") prev.items.push(ol[1]);
      else blocks.push({ kind: "ol", items: [ol[1]] });
      continue;
    }
    const ul = /^[-*]\s+(.*)$/.exec(line);
    if (ul) {
      flushPara();
      const prev = blocks[blocks.length - 1];
      if (prev && prev.kind === "ul") prev.items.push(ul[1]);
      else blocks.push({ kind: "ul", items: [ul[1]] });
      continue;
    }
    para.push(line);
  }
  flushPara();
  return blocks;
}

export function Markdown({ text }: { text: string }) {
  const blocks = parseBlocks(text);
  return (
    <div className="space-y-2 text-[13px] leading-relaxed text-foreground">
      {blocks.map((b, i) => {
        switch (b.kind) {
          case "h":
            return b.level === 2 ? (
              <h4 key={i} className="text-sm font-semibold tracking-tight">
                {renderInline(b.text, `h${i}`)}
              </h4>
            ) : (
              <h5 key={i} className="text-[13px] font-semibold">
                {renderInline(b.text, `h${i}`)}
              </h5>
            );
          case "ol":
            return (
              <ol key={i} className="ml-5 list-decimal space-y-1">
                {b.items.map((it, j) => (
                  <li key={j}>{renderInline(it, `ol${i}-${j}`)}</li>
                ))}
              </ol>
            );
          case "ul":
            return (
              <ul key={i} className="ml-5 list-disc space-y-1">
                {b.items.map((it, j) => (
                  <li key={j}>{renderInline(it, `ul${i}-${j}`)}</li>
                ))}
              </ul>
            );
          default:
            return (
              <p key={i}>
                <Fragment>{renderInline(b.text, `p${i}`)}</Fragment>
              </p>
            );
        }
      })}
    </div>
  );
}
